import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { mediaSrc, avatarSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, MessageCircle, Eye, Volume2, VolumeX, Share2, Loader2, Play, Plus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { VerifiedBadge } from "./VerifiedBadge";

const PAGE = 8;

export default function FeedFYP({ mediaType = "", following = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [muted, setMuted] = useState(true);
  const [active, setActive] = useState(0);
  const scrollRef = useRef(null);
  const seenRef = useRef(new Set());

  const fetchBatch = useCallback(async (reset) => {
    const exclude = reset ? "" : Array.from(seenRef.current).slice(-200).join(",");
    const { data } = await api.get("/feed", { params: { exclude, limit: PAGE, media_type: mediaType, following } });
    return data;
  }, [mediaType, following]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    seenRef.current = new Set();
    fetchBatch(true).then((data) => {
      if (!alive) return;
      data.forEach((d) => seenRef.current.add(d.id));
      setItems(data);
      setHasMore(data.length === PAGE);
      setActive(0);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [fetchBatch]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchBatch(false);
      const fresh = data.filter((d) => !seenRef.current.has(d.id));
      fresh.forEach((d) => seenRef.current.add(d.id));
      setItems((prev) => [...prev, ...fresh]);
      setHasMore(data.length === PAGE && fresh.length > 0);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, fetchBatch]);

  useEffect(() => {
    if (active >= items.length - 3) loadMore();
  }, [active, items.length, loadMore]);

  const like = async (item, idx) => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.post(`/media/${item.id}/like`);
      setItems((prev) => prev.map((m, i) => (i === idx ? { ...m, liked: data.liked, likes_count: data.likes_count } : m)));
    } catch (e) { console.error("like failed", e); }
  };

  const follow = async (item, idx) => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.post(`/users/${item.author_id}/follow`);
      setItems((prev) => prev.map((m, i) => (m.author_id === item.author_id ? { ...m, author_followed: data.following } : m)));
    } catch (e) { console.error("follow failed", e); }
  };

  const onViewed = (idx) => {
    setItems((prev) => prev.map((m, i) => (i === idx ? { ...m, views: (m.views || 0) + 1 } : m)));
  };

  const share = (item) => {
    const url = `${window.location.origin}/api/og/${item.id}`;
    if (navigator.share) navigator.share({ title: item.title, url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success("Link copied"); }
  };

  if (loading) return <div className="flex justify-center items-center h-[calc(100dvh-3.25rem)]"><Loader2 className="animate-spin text-[#0066FF]" size={32} /></div>;

  if (items.length === 0) return (
    <div className="flex flex-col justify-center items-center h-[calc(100dvh-3.25rem)] text-center px-6">
      <p className="font-heading text-3xl uppercase text-[#2A2A2E] mb-3">{following ? "Your feed is empty" : "Nothing here yet"}</p>
      <p className="text-[#52525B] text-sm mb-6">{following ? "Follow creators to see their drops." : "Be the first to drop something."}</p>
      <Link to="/upload" className="bg-[#0066FF] text-white font-heading uppercase tracking-widest text-sm px-8 py-3 hover:bg-[#0055CC] transition-colors">Upload Media</Link>
    </div>
  );

  return (
    <div ref={scrollRef} data-testid="fyp-feed" className="fyp-scroll h-[calc(100dvh-3.25rem)] overflow-y-scroll snap-y snap-mandatory bg-black" style={{ scrollbarWidth: "none" }}>
      <style>{`.fyp-scroll::-webkit-scrollbar{display:none}
        @keyframes heartburst{0%{transform:translate(-50%,-50%) scale(0);opacity:0}15%{opacity:1}40%{transform:translate(-50%,-50%) scale(1.15)}100%{transform:translate(-50%,-50%) scale(1.4);opacity:0}}
        .heart-burst{animation:heartburst .8s ease-out forwards}`}</style>
      {items.map((item, i) => (
        <Slide
          key={item.id}
          item={item}
          index={i}
          active={active === i}
          muted={muted}
          setMuted={setMuted}
          setActive={setActive}
          user={user}
          onLike={() => like(item, i)}
          onFollow={() => follow(item, i)}
          onShare={() => share(item)}
          onViewed={() => onViewed(i)}
        />
      ))}
      {loadingMore && (
        <div className="h-16 flex items-center justify-center"><Loader2 className="animate-spin text-[#0066FF]" size={22} /></div>
      )}
    </div>
  );
}

function Slide({ item, index, active, muted, setMuted, setActive, user, onLike, onFollow, onShare, onViewed }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [bursts, setBursts] = useState([]);
  const startRef = useRef(null);
  const sentRef = useRef(false);
  const isVideo = item.media_type === "video";
  const isOwn = user && (user.id === item.author_id);

  const sendView = () => {
    if (sentRef.current || !startRef.current) return;
    const dwell = Date.now() - startRef.current;
    if (dwell < 700) return;
    sentRef.current = true;
    api.post(`/media/${item.id}/view`, { dwell_ms: dwell }).catch(() => {});
    onViewed();
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && entries[0].intersectionRatio > 0.6) setActive(index);
    }, { threshold: [0.6] });
    obs.observe(el);
    return () => obs.disconnect();
  }, [index, setActive]);

  useEffect(() => {
    const v = videoRef.current;
    if (active) {
      startRef.current = Date.now();
      if (v) v.play().then(() => setPaused(false)).catch(() => {});
    } else {
      if (v) v.pause();
      sendView();
      startRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => () => sendView(), []); // flush on unmount

  const burstAt = (clientX, clientY) => {
    const rect = ref.current?.getBoundingClientRect();
    const x = rect ? clientX - rect.left : 0;
    const y = rect ? clientY - rect.top : 0;
    const id = Date.now() + Math.random();
    setBursts((b) => [...b, { id, x, y }]);
    setTimeout(() => setBursts((b) => b.filter((z) => z.id !== id)), 800);
  };

  const doLike = (e) => {
    if (e) burstAt(e.clientX, e.clientY);
    onLike();
  };

  const tapMedia = () => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPaused(false); } else { v.pause(); setPaused(true); }
  };

  const lastTapRef = useRef(0);
  const handleClick = (e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) { doLike(e); lastTapRef.current = 0; }
    else { lastTapRef.current = now; setTimeout(() => { if (Date.now() - lastTapRef.current >= 300) tapMedia(); }, 310); }
  };

  return (
    <section ref={ref} data-testid={`fyp-slide-${index}`} className="h-full w-full snap-start snap-always relative flex items-center justify-center">
      {/* centered phone-width column */}
      <div className="relative h-full w-full sm:max-w-[480px] sm:mx-auto bg-black flex items-center justify-center overflow-hidden">
        {isVideo ? (
          <video
            ref={videoRef}
            src={mediaSrc(item.url)}
            poster={item.thumb_url ? mediaSrc(item.thumb_url) : undefined}
            muted={muted}
            loop
            playsInline
            onClick={handleClick}
            className="h-full w-full object-contain cursor-pointer"
            data-testid={`fyp-video-${index}`}
          />
        ) : (
          <img src={mediaSrc(item.url)} alt={item.ai_caption || item.title} onDoubleClick={(e) => doLike(e)} className="h-full w-full object-contain" />
        )}

        {/* double-tap heart bursts */}
        {bursts.map((b) => (
          <Heart key={b.id} size={90} fill="#0066FF" className="heart-burst text-[#0066FF] absolute pointer-events-none z-20 drop-shadow-lg" style={{ left: b.x, top: b.y }} />
        ))}

        {isVideo && paused && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Play size={64} className="text-white/70" fill="white" />
          </div>
        )}

        {/* gradient for legibility */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        {/* bottom-left info */}
        <div className="absolute bottom-0 left-0 right-16 p-4 z-10">
          <div className="flex items-center gap-2 mb-2">
            <Link to={`/u/${item.author_username}`} className="inline-flex items-center gap-2 group">
              {item.author_avatar ? (
                <img src={avatarSrc(item.author_avatar)} alt="" className="w-9 h-9 object-cover border border-white/30 rounded-full" />
              ) : (
                <div className="w-9 h-9 bg-[#0066FF] flex items-center justify-center font-heading text-white text-sm rounded-full">{item.author_username?.[0]?.toUpperCase()}</div>
              )}
              <span className="flex flex-col leading-tight">
                <span className="text-white text-sm font-semibold group-hover:text-[#0066FF] transition-colors flex items-center gap-1">{item.author_display_name || item.author_username}<VerifiedBadge show={item.author_verified} size={14} testId={`fyp-verified-${index}`} /></span>
                <span className="text-white/60 text-xs">@{item.author_username}</span>
              </span>
            </Link>
            {!isOwn && (
              item.author_followed ? (
                <span className="flex items-center gap-1 text-[#3fb950] font-mono text-[10px] uppercase tracking-wider"><UserCheck size={12} /> Following</span>
              ) : (
                <button onClick={onFollow} data-testid={`fyp-follow-${index}`} className="flex items-center gap-1 bg-[#0066FF] hover:bg-[#0055CC] text-white font-heading uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                  <Plus size={11} /> Follow
                </button>
              )
            )}
          </div>
          <Link to={`/media/${item.id}`} className="block">
            <h3 className="text-white font-heading uppercase tracking-tight text-lg leading-tight line-clamp-2">{item.title}</h3>
          </Link>
          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {item.tags.slice(0, 4).map((t) => (
                <Link key={t} to={`/explore?tag=${t}`} className="font-mono text-[10px] text-[#FFFFFF] hover:text-[#0066FF] transition-colors">#{t}</Link>
              ))}
            </div>
          )}
        </div>

        {/* right action rail */}
        <div className="absolute right-2 bottom-6 z-10 flex flex-col items-center gap-5">
          <button onClick={(e) => doLike(e)} data-testid={`fyp-like-${index}`} className="flex flex-col items-center gap-1 group">
            <span className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${item.liked ? "bg-[#0066FF] border-[#0066FF]" : "bg-black/50 border-white/20 group-hover:border-white"}`}>
              <Heart size={22} className="text-white" fill={item.liked ? "white" : "none"} />
            </span>
            <span className="text-white text-[11px] font-mono">{item.likes_count}</span>
          </button>
          <Link to={`/media/${item.id}`} className="flex flex-col items-center gap-1 group">
            <span className="w-11 h-11 rounded-full bg-black/50 border border-white/20 group-hover:border-white flex items-center justify-center transition-colors">
              <MessageCircle size={22} className="text-white" />
            </span>
            <span className="text-white text-[11px] font-mono">{item.comments_count}</span>
          </Link>
          <div className="flex flex-col items-center gap-1">
            <span className="w-11 h-11 rounded-full bg-black/50 border border-white/20 flex items-center justify-center">
              <Eye size={20} className="text-white" />
            </span>
            <span className="text-white text-[11px] font-mono">{item.views}</span>
          </div>
          <button onClick={onShare} data-testid={`fyp-share-${index}`} className="w-11 h-11 rounded-full bg-black/50 border border-white/20 hover:border-white flex items-center justify-center transition-colors">
            <Share2 size={20} className="text-white" />
          </button>
          {isVideo && (
            <button onClick={() => setMuted((m) => !m)} data-testid={`fyp-mute-${index}`} className="w-11 h-11 rounded-full bg-black/50 border border-white/20 hover:border-white flex items-center justify-center transition-colors">
              {muted ? <VolumeX size={20} className="text-white" /> : <Volume2 size={20} className="text-white" />}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
